'use client';
import React from 'react';
import Cookies from 'js-cookie';

import { BlockWrapper } from '@/components/block-wrapper/block-wrapper';
import { Breadcrumbs } from '@/components/breadcrumbs';
import { PageComponentImage } from '@/components/page-components/image/page-component-image';
import { PageComponentMainBottomBorder } from '@/components/page-components/main/page-component-main-bottom-border';
import { PageComponentMainHeader } from '@/components/page-components/main/page-component-main-header';
import TenantDescription from '@/components/page-components/tenant-detail-page/tenant-description';
import ViewOnMap from '@/components/page-components/tenant-detail-page/view-on-map';
import { TenantInfo } from '@/components/page-components/tenant-info/tenant-info';
import { Share } from '@/components/share/share';
import { FavouriteButton } from '@/components/ui/favourite-button/favourite-button';
import LinkButton from '@/components/ui/link-button/link-button';
import useGetMobileAppWebViewHeader from '@/hooks/use-get-mobile-app-web-view';
import { useIsMobile } from '@/hooks/use-is-mobile';
import { IGtmParameters, useSendGtmEvent } from '@/hooks/use-send-gtm-event';
import useNewsletterPopupAutomation from '@/hooks/useNewsletterPopupAutomation';
import { IMeetingplacePageHomeMinifiedExtendedOpeningHours } from '@/lib/fragments/meetingPlacePageHome-minified';
import { IMeetingPlacePageServiceItem } from '@/lib/fragments/meetingPlacePageServiceItem';
import { useTenantConfig } from '@/providers/ConfigProvider';
import { GtmPageView } from '@/components/page-components/analytics/gtm-page-view';
import Button from '@/components/ui/button/button';
import { useProfileSlider } from '@/providers/ProfileSliderProvider';
import { useUser } from '@/providers/UserProvider';
import { redirect } from 'next/navigation';
import { isBookingSubdomain } from '@/helpers/utils';

interface MeetingPlacePageServiceProps {
  meetingPlacePageService: IMeetingPlacePageServiceItem;
  meetingPlacePageHome: IMeetingplacePageHomeMinifiedExtendedOpeningHours;
}

const MeetingPlacePageService = ({ metaData, node_locale, data }: any) => {
  const config = useTenantConfig();
  const page = data?.meetingPlacePageService;
  const homepage = data?.meetingPlacePageHome;
  const isLivliFamily = config?.NEXT_PUBLIC_IS_MP_LIVLI_FAMILY === 'true';
  const { user } = useUser();
  // const user_token = Cookies.get('user_token');
  const { openSlider } = useProfileSlider();

  const pageName = page?.name ? page.name : page?.serviceType?.name || '';
  const parent = data?.serviceListPage;
  const image =
    page?.serviceType &&
    page.serviceType?.coverImageWrapper?.jsonBynderAsset?.originalUrl
      ? page.serviceType?.coverImageWrapper?.jsonBynderAsset?.originalUrl
      : undefined;

  const isMobile = useIsMobile();
  const isWebView = useGetMobileAppWebViewHeader();

  const gtmParams: IGtmParameters = {
    event: 'view_service_detail',
    detail_name: pageName,
    contentful_id: page?.sys?.id || '',
    offer_type: 'Offer Type Not Available',
    user_status: isLivliFamily ? (user ? 'logged_in' : 'guest') : undefined,
  };
  useSendGtmEvent(gtmParams);

  useNewsletterPopupAutomation();

  const onBookingEngineButtonClick = (bookingUrl: string) => {
    const url = new URL(bookingUrl);
    const pathWithQuery = url.pathname + url.search;
    if (user) {
      if (bookingUrl) {
        redirect(pathWithQuery + '?isHosted=true');
        // window.open(`${window.location.origin}${pathWithQuery}`, '_blank');
      }
    } else {
      openSlider(pathWithQuery + '?isHosted=true');
    }
  };

  return (
    <>
      {page && (
        <>
          {/* ✅ GTM PAGE VIEW */}
          <GtmPageView
            homepage={homepage}
            language={node_locale}
            pageTitle={page?.name ?? ''}
          />

          {isMobile ? (
            <>
              <div className="w-full rounded-b-xxl bg-background-primary-light px-6 pb-8">
                <Breadcrumbs
                  breadcrumbs={[pageName ?? '']}
                  homeText={homepage?.breadcrumbHomeText ?? ''}
                  cssClass={`lg:ml-22 ${isWebView ? '!pt-8' : ''}`}
                  node_locale={node_locale}
                  parent={parent}
                />
                <div className="pt-5">
                  <PageComponentImage
                    imageURL={image}
                    hasColoredBackground={true}
                    node_locale={page?.sys?.locale}
                  />
                </div>
                <div className="mt-6">
                  <div className="flex items-center justify-between">
                    <PageComponentMainHeader header={pageName ?? ''} />
                    <FavouriteButton
                      id={page?.sys?.id ?? ''}
                      type="service"
                      name={pageName ?? ''}
                    />
                  </div>
                  {isLivliFamily &&
                    page.bookingButtonText &&
                    isBookingSubdomain(page?.bookingUrl) && (
                      <Button
                        className={`mb-8 mt-4`}
                        onClick={() => {
                          onBookingEngineButtonClick(page?.bookingUrl);
                        }}
                      >
                        {page.bookingButtonText}
                      </Button>
                    )}
                  {(!isLivliFamily || !isBookingSubdomain(page?.bookingUrl)) &&
                    page?.bookingButtonText &&
                    page?.bookingUrl && (
                      <LinkButton
                        to={page?.bookingUrl}
                        className="mt-4 w-full text-center"
                        openInNewTab
                        isExternalLink
                      >
                        {page.bookingButtonText}
                      </LinkButton>
                    )}
                  {homepage?.meetingPlace?.showSocialShare && (
                    <Share className="relative top-4 ml-auto" />
                  )}
                </div>
              </div>
              <div className="mt-8 px-6">
                {homepage && (
                  <TenantInfo
                    page={page}
                    homepage={homepage}
                    node_locale={page?.sys?.locale}
                    isMobile={isMobile}
                    variant="topInfo"
                  />
                )}

                {page?.floorPlanIDs?.length > 0 &&
                  homepage?.meetingPlace?.visioGlobeMapKey &&
                  homepage.meetingPlace.visioGlobeFloorPlanPage && (
                    <ViewOnMap page={page} homepage={homepage} />
                  )}
                {page.description && (
                  <TenantDescription
                    description={page.description}
                    node_locale={node_locale}
                  />
                )}
                <div className="-mx-6 mt-10 bg-brand-white px-6 lg:m-0 lg:p-0">
                  {homepage && (
                    <TenantInfo
                      page={page}
                      homepage={homepage}
                      node_locale={page?.sys?.locale}
                      isMobile={isMobile}
                      variant="bottomInfo"
                    />
                  )}
                </div>
              </div>
            </>
          ) : (
            <main className="bg-background-primary-light px-6 lg:-ml-22 lg:bg-transparent lg:px-0">
              <Breadcrumbs
                breadcrumbs={[pageName ?? '']}
                homeText={homepage?.breadcrumbHomeText ?? ''}
                cssClass="lg:ml-22"
                node_locale={node_locale}
                parent={parent}
              />
              <div className="grid grid-cols-11 gap-6 pt-5 lg:gap-5">
                <div className="col-span-12 bg-background-primary-light lg:col-span-5 lg:bg-transparent">
                  <div className="lg:sticky lg:top-0">
                    <PageComponentImage
                      imageURL={image}
                      hasColoredBackground={true}
                      node_locale={page?.sys?.locale}
                    />
                    {homepage && (
                      <TenantInfo
                        page={page}
                        homepage={homepage}
                        node_locale={page?.sys?.locale}
                        isMobile={isMobile}
                      />
                    )}
                  </div>
                </div>
                <div className="col-span-12 lg:col-span-6">
                  <BlockWrapper>
                    <div className="flex items-center justify-between mb-4">
                      <PageComponentMainHeader header={pageName ?? ''} />
                      <FavouriteButton
                        id={page?.sys?.id ?? ''}
                        type="service"
                        name={pageName ?? ''}
                      />
                    </div>
                    <PageComponentMainBottomBorder />
                    {isLivliFamily &&
                      page.bookingButtonText &&
                      isBookingSubdomain(page?.bookingUrl) && (
                        <Button
                          className={`mb-8 mt-4`}
                          onClick={() => {
                            onBookingEngineButtonClick(page?.bookingUrl);
                          }}
                        >
                          {page?.bookingButtonText}
                        </Button>
                      )}
                    {(!isLivliFamily ||
                      !isBookingSubdomain(page?.bookingUrl)) &&
                      page.bookingButtonText &&
                      page?.bookingUrl && (
                        <LinkButton
                          to={page?.bookingUrl}
                          className="mb-8 mt-4"
                          openInNewTab
                          isExternalLink
                        >
                          {page?.bookingButtonText}
                        </LinkButton>
                      )}
                    {page.description && (
                      <TenantDescription
                        description={page.description}
                        node_locale={page?.sys?.locale}
                      />
                    )}
                    {page?.floorPlanIDs?.length > 0 &&
                      homepage?.meetingPlace?.visioGlobeMapKey &&
                      homepage.meetingPlace.visioGlobeFloorPlanPage && (
                        <ViewOnMap page={page} homepage={homepage} />
                      )}
                    {homepage?.meetingPlace?.showSocialShare && (
                      <Share className="absolute -bottom-2 right-0 lg:bottom-6 lg:right-20 xl:right-24" />
                    )}
                  </BlockWrapper>
                </div>
              </div>
            </main>
          )}
        </>
      )}
    </>
  );
};
export default MeetingPlacePageService;

// export const query = graphql`
//   query MeetingPlacePageTenantServiceQuery(
//     $id: String!
//     $language: String!
//     $tagId: String!
//     $languages: [String!]
//   ) {
//     contentfulMeetingPlacePageServiceItem(
//       id: { eq: $id }
//       node_locale: { in: $languages }
//     ) {
//       ...MeetingPlacePageServiceItem
//     }
//     contentfulMeetingPlacePageHome(
//       metadata: { tags: { elemMatch: { contentful_id: { eq: $tagId } } } }
//       node_locale: { eq: $language }
//     ) {
//       ...MeetingPlacePageHomeMinifiedExtendedOpeningHours
//     }
//   }
// `;
